import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import s from './GoBack.module.scss';

/**
 * GoBack component - Provides navigation back functionality with fallbacks
 * 
 * @param {Object} props - Component props
 * @param {string} [props.returnUrl] - Direct URL to navigate to instead of using history
 * @param {string} [props.defaultLink] - Default URL to navigate to if history is not available
 * @param {string} [props.customClassName] - Additional CSS class name(s)
 * @param {React.ReactNode} props.children - Button/link content
 */
const GoBack = props => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleGoBack = () => {
    // Check if we have state to go back to
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      // No history to go back to, use provided defaultLink or fallback to dashboard
      navigate(props.defaultLink || '/dashboard');
    }
  };

  // If returnUrl is provided, render a Link
  if (props.returnUrl) {
    return (
      <div className={s.GoBackWrapper}>
        <Link 
          className={`${s.backLink} ${props.customClassName || ''}`} 
          to={props.returnUrl}
        >
          {props.children}
        </Link>
      </div>
    );
  }

  // Otherwise, render a button that uses history
  return (
    <div className={s.GoBackWrapper}>
      <button
        className={`${s.backLink} ${props.customClassName || ''}`}
        type="button"
        onClick={handleGoBack}
        aria-label="Go back"
      >
        {props.children}
      </button>
    </div>
  );
};

GoBack.propTypes = {
  returnUrl: PropTypes.string,
  defaultLink: PropTypes.string,
  customClassName: PropTypes.string,
  children: PropTypes.node.isRequired
};

export default GoBack;